import type { Message } from '@/types';
import { MessageSender } from '@/types';

/**
 * Suggestion item attached to an AI message (id + display text).
 */
export type SuggestedQuestion = NonNullable<Message['suggested_questions']>[number];

const hasSuggestions = (msg: Message | undefined): msg is Message =>
  !!msg &&
  msg.sender_type === MessageSender.AI &&
  !!msg.suggested_questions &&
  msg.suggested_questions.length > 0;

const lastIndex = (
  messages: Message[],
  predicate: (msg: Message | undefined) => boolean
): number => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (predicate(messages[i])) return i;
  }
  return -1;
};

/**
 * Invokes the suggestion-select callback with the chosen question text.
 */
export const handleQuestionClick = (text: string, onSelect?: (text: string) => void): void => {
  onSelect?.(text);
};

/**
 * Returns the AI message whose suggestions should currently render.
 *
 * The latest AI turn wins when it carries non-empty suggestions; otherwise the
 * previous AI turn with suggestions is used as a fallback so the user always
 * has something to interact with after a fail-to-generate response.
 */
export const findMessageWithSuggestions = (messages: Message[]): Message | undefined => {
  const latestAiIdx = lastIndex(messages, (msg) => msg?.sender_type === MessageSender.AI);
  if (latestAiIdx === -1) return undefined;

  const latest = messages[latestAiIdx];
  if (hasSuggestions(latest)) return latest;

  for (let i = latestAiIdx - 1; i >= 0; i--) {
    const msg = messages[i];
    if (hasSuggestions(msg)) return msg;
  }
  return undefined;
};

/**
 * Convenience accessor returning the active suggestions array directly. Empty
 * when neither the latest nor any prior AI turn carries suggestions.
 */
export const getActiveSuggestions = (messages: Message[]): SuggestedQuestion[] =>
  findMessageWithSuggestions(messages)?.suggested_questions ?? [];
